import { useState, useRef, useEffect, useMemo  } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import planbLogo from '../assets/planb-logo.png';
import api, { BACKEND_URL } from '../api/axios';
import { SKILLS_LIST, JOB_TITLES_LIST,  } from '../data/Autocompletedata';
import { getStoredAttribution } from '../utils/Attribution';
import GoogleSignInButton from '../pages/GoogleSignInButton';

/* ============================================================================
   PlanB Jobs — Conversational Registration (v4, "Boo-style" chat/dating-app pass)
   ----------------------------------------------------------------------------
   Same step machine, same handlers, same state, same API calls as before.
   This pass ONLY replaces the presentational layer (classNames, keyframes,
   markup of purely-decorative wrappers) to read like a playful matchmaking
   chat app (à la Boo: Dating. Friends. Chat.):

     - Chunky full-bleed gradient "match cards" instead of flat icon tiles
     - Real chat-bubble tails + springier bounce-in
     - A segmented "story bar" progress indicator instead of a plain strip
     - A friendlier header with an "online now" pulse on the bot avatar
     - Chunkier OTP boxes, glowing send button, confetti on the final step
     - A rounder, more characterful display font for headings/labels

   Nothing about the step order, validation, network calls, or form state
   was touched — search for handler/useEffect blocks below and diff against
   the previous version if you want to confirm.
   ============================================================================ */

const OTP_LENGTH = 4; // matches existing OtpController
const RESEND_SECONDS = 30;
const MIN_LOCATIONS = 5;
const MAX_LOCATIONS = 10;
const MIN_SKILLS = 5;
const MAX_SKILLS = 10;
const MIN_LOOKING_ROLES = 5;
const MAX_LOOKING_ROLES = 10;
const MIN_TARGET_ROLES = 5;
const MAX_TARGET_ROLES = 10;
const MAX_PREVIOUS_EMPLOYERS = 3;
const SKILL_SUGGESTIONS_VISIBLE = 6;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS = Array.from({ length: 45 }, (_, i) => new Date().getFullYear() - i);

// Small glyphs shown on each gender pill instead of a plain color dot.
const GENDER_ICONS = {
  Male: '♂️',
  Female: '♀️',
  Other: '⚧️',
  'Prefer not to say': '🤐',
};

// Full-bleed gradient "match card" palette — cycles through these so each
// quick-reply card reads as a distinct, tappable swipe-card rather than a
// plain outlined button.
const CARD_STYLES = [
  { bg: 'linear-gradient(135deg,#7C3AED,#A855F7)', ring: 'rgba(124,58,237,0.45)' },
  { bg: 'linear-gradient(135deg,#DB2777,#EC4899)', ring: 'rgba(219,39,119,0.45)' },
  { bg: 'linear-gradient(135deg,#C026D3,#E879F9)', ring: 'rgba(192,38,211,0.45)' },
  { bg: 'linear-gradient(135deg,#6D28D9,#9333EA)', ring: 'rgba(109,40,217,0.45)' },
];

// Floating decorative glyphs behind the "device" card — purely cosmetic,
// no interaction, no logic. Positions/delays are fixed so layout is stable.
const FLOATERS = [
  { glyph: '💼', top: '8%',  left: '6%',  size: 24, delay: '0s',   dur: '7s' },
  { glyph: '✨', top: '16%', left: '87%', size: 20, delay: '1.2s', dur: '6s' },
  { glyph: '📈', top: '70%', left: '91%', size: 22, delay: '0.6s', dur: '8s' },
  { glyph: '⭐', top: '82%', left: '9%',  size: 18, delay: '2s',   dur: '6.5s' },
  { glyph: '🎯', top: '38%', left: '3%',  size: 20, delay: '1.6s', dur: '7.5s' },
  { glyph: '✅', top: '54%', left: '95%', size: 16, delay: '0.3s', dur: '9s' },
  { glyph: '🚀', top: '4%',  left: '46%', size: 16, delay: '2.4s', dur: '8.5s' },
];

// Confetti burst shown once, on the final "summary" step — purely cosmetic.
const CONFETTI = [
  { glyph: '🎉', left: '10%',  delay: '0s',    dur: '2.6s', size: 20 },
  { glyph: '💼', left: '24%',  delay: '0.15s', dur: '2.2s', size: 16 },
  { glyph: '✨', left: '40%',  delay: '0.05s', dur: '2.8s', size: 18 },
  { glyph: '📈', left: '58%',  delay: '0.3s',  dur: '2.4s', size: 16 },
  { glyph: '🎊', left: '74%',  delay: '0.1s',  dur: '2.6s', size: 20 },
  { glyph: '🎯', left: '88%',  delay: '0.25s', dur: '2.3s', size: 16 },
];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// Collapses whitespace/casing differences and strips a trailing ", State"
// so "bengaluru", "Bengaluru", "  BENGALURU  ", and "Bengaluru, Karnataka"
// all normalize to the same canonical string before being stored.
function normalizeCityName(raw) {
  if (!raw) return '';
  let v = raw.trim().replace(/\s+/g, ' ');
  // Drop a trailing ", State" or ", State, Country" (AI suggestions return
  // "City, State" — we only want the city part for comparison/storage).
  v = v.split(',')[0].trim();
  // Title-case each word so "bengaluru" -> "Bengaluru", "new delhi" -> "New Delhi".
  v = v.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  return v;
}

const PROGRESS_KEY_PREFIX = 'planb_register_progress_';

function saveProgress(phone, data) {
  if (!phone) return;
  try { localStorage.setItem(PROGRESS_KEY_PREFIX + phone, JSON.stringify(data)); }
  catch (e) { /* storage full or disabled — safe to ignore */ }
}
function loadProgress(phone) {
  if (!phone) return null;
  try {
    const raw = localStorage.getItem(PROGRESS_KEY_PREFIX + phone);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}
function clearProgress(phone) {
  if (!phone) return;
  try { localStorage.removeItem(PROGRESS_KEY_PREFIX + phone); } catch (e) {}
}

// Steps before this point don't need to be "resumed" — if the user dropped
// off before OTP verification, no DB row exists yet and they'll just start
// fresh next time anyway.
const NON_RESUMABLE_STEPS = ['welcome', 'name', 'gender', 'current_location', 'phone', 'otp'];

/* ---------------------------------------------------------------------- */
/* Global keyframes (scoped via a plain <style> tag — no Tailwind config    */
/* changes needed in the host project)                                     */
/* ---------------------------------------------------------------------- */

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&display=swap');

      :root { --rc-font-display: 'Baloo 2', ui-rounded, 'Segoe UI', system-ui, sans-serif; }

      @keyframes rc-pop-in {
        0%   { opacity: 0; transform: translateY(18px) scale(0.8) rotate(-1.5deg); }
        55%  { opacity: 1; transform: translateY(-4px) scale(1.05) rotate(0.5deg); }
        80%  { transform: translateY(1px) scale(0.99) rotate(0deg); }
        100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
      }
      @keyframes rc-composer-in {
        0%   { opacity: 0; transform: translateY(22px) scale(0.94); }
        65%  { opacity: 1; transform: translateY(-3px) scale(1.015); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes rc-blob {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(24px,-18px) scale(1.08); }
      }
      @keyframes rc-float {
        0%,100% { transform: translateY(0) rotate(0deg); }
        50%     { transform: translateY(-16px) rotate(10deg); }
      }
      @keyframes rc-wave {
        0%, 100% { transform: rotate(0deg); }
        15%      { transform: rotate(-14deg); }
        30%      { transform: rotate(12deg); }
        45%      { transform: rotate(-10deg); }
        60%      { transform: rotate(8deg); }
        75%      { transform: rotate(0deg); }
      }
      @keyframes rc-glow-pulse {
        0%,100% { box-shadow: 0 4px 14px rgba(124,58,237,0.35), 0 0 0 0 rgba(236,72,153,0.35); }
        50%     { box-shadow: 0 6px 22px rgba(124,58,237,0.5), 0 0 0 10px rgba(236,72,153,0); }
      }
      @keyframes rc-shimmer {
        0%   { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes rc-segment-fill {
        0%   { transform: scaleX(0); }
        100% { transform: scaleX(1); }
      }
      @keyframes rc-card-in {
        0%   { opacity: 0; transform: scale(0.9) rotate(-2deg) translateY(28px); }
        100% { opacity: 1; transform: scale(1) rotate(0deg) translateY(0); }
      }
      @keyframes rc-dot-bounce {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
        30%           { transform: translateY(-6px); opacity: 1; }
      }
      @keyframes rc-heartbeat {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.2); }
      }
      @keyframes rc-online-ring {
        0%   { box-shadow: 0 0 0 0 rgba(52,211,153,0.55); }
        100% { box-shadow: 0 0 0 9px rgba(52,211,153,0); }
      }
      @keyframes rc-tail-pop {
        0%   { opacity: 0; transform: scale(0.4); }
        100% { opacity: 1; transform: scale(1); }
      }
      @keyframes rc-confetti-fall {
        0%   { opacity: 0; transform: translateY(-12px) rotate(0deg); }
        12%  { opacity: 1; }
        100% { opacity: 0; transform: translateY(140px) rotate(220deg); }
      }
      @keyframes rc-sparkle-float {
        0%   { opacity: 0; transform: translateY(6px) scale(0.6); }
        30%  { opacity: 1; }
        100% { opacity: 0; transform: translateY(-22px) scale(1); }
      }
      @keyframes rc-otp-pop {
        0%   { transform: scale(1); }
        40%  { transform: scale(1.18); }
        100% { transform: scale(1); }
      }

      .rc-msg { animation: rc-pop-in 0.48s cubic-bezier(0.34, 1.61, 0.64, 1) both; }
      .rc-composer { animation: rc-composer-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      .rc-blob { animation: rc-blob 9s ease-in-out infinite; }
      .rc-floater { position: absolute; animation: rc-float ease-in-out infinite; pointer-events: none; user-select: none; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.15)); }
      .rc-card { animation: rc-card-in 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      .rc-avatar-wave { animation: rc-wave 2.4s ease-in-out infinite; transform-origin: 70% 70%; display: inline-block; }
      .rc-online-dot { animation: rc-online-ring 1.8s ease-out infinite; }
      .rc-send-btn:hover:not(:disabled) { animation: rc-glow-pulse 1.3s ease-in-out infinite; }
      .rc-progress-seg-fill {
        transform-origin: left center;
        animation: rc-segment-fill 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        background-image: linear-gradient(90deg, #7C3AED, #C026D3, #EC4899, #C026D3, #7C3AED);
        background-size: 220% 100%;
        animation: rc-segment-fill 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both, rc-shimmer 2.6s linear infinite 0.5s;
      }
      .rc-dot { animation: rc-dot-bounce 1.1s ease-in-out infinite; }
      .rc-heart-beat { display: inline-block; animation: rc-heartbeat 1.15s ease-in-out infinite; }
      .rc-bubble-tail { animation: rc-tail-pop 0.3s ease-out 0.15s both; }
      .rc-confetti-piece { position: absolute; top: 0; animation: rc-confetti-fall ease-in forwards; pointer-events: none; }
      .rc-otp-filled { animation: rc-otp-pop 0.28s ease-out; }

      .rc-font-display { font-family: var(--rc-font-display); letter-spacing: 0.01em; }

      .rc-tile {
        transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease;
        transform: rotate(var(--rc-tilt, 0deg));
      }
      .rc-tile:hover { transform: rotate(0deg) translateY(-6px) scale(1.035); box-shadow: 0 18px 34px -10px var(--rc-tile-ring, rgba(124,58,237,0.5)); }
      .rc-tile:active { transform: translateY(-1px) scale(0.97) rotate(0deg); }

      .rc-pill { transition: transform 0.2s ease, box-shadow 0.2s ease; }
      .rc-pill:hover { transform: translateY(-2px) scale(1.03); }
      .rc-pill:active { transform: scale(0.97); }

      .rc-primary-btn { position: relative; transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease; overflow: visible; }
      .rc-primary-btn:hover:not(:disabled) { transform: translateY(-2px) scale(1.02); }
      .rc-primary-btn:active:not(:disabled) { transform: translateY(0) scale(0.98); }
      .rc-primary-btn:hover:not(:disabled)::before,
      .rc-primary-btn:hover:not(:disabled)::after {
        content: '✨'; position: absolute; top: -6px; font-size: 12px; opacity: 0;
        animation: rc-sparkle-float 1.1s ease-in-out infinite;
      }
      .rc-primary-btn:hover:not(:disabled)::before { left: 14%; animation-delay: 0.05s; }
      .rc-primary-btn:hover:not(:disabled)::after { right: 14%; animation-delay: 0.4s; }

      .rc-chip-in { animation: rc-pop-in 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both; }

      @media (prefers-reduced-motion: reduce) {
        .rc-msg, .rc-composer, .rc-blob, .rc-floater, .rc-card, .rc-avatar-wave,
        .rc-online-dot, .rc-send-btn:hover, .rc-progress-seg-fill, .rc-dot, .rc-heart-beat,
        .rc-tile, .rc-pill, .rc-primary-btn, .rc-chip-in, .rc-bubble-tail, .rc-confetti-piece,
        .rc-otp-filled, .rc-primary-btn:hover:not(:disabled)::before, .rc-primary-btn:hover:not(:disabled)::after {
          animation: none !important;
          transition: none !important;
        }
        .rc-tile:hover { transform: none; }
      }
    `}</style>
  );
}

/* ---------------------------------------------------------------------- */
/* Presentational bits                                                     */
/* ---------------------------------------------------------------------- */

function BotAvatar() {
  return (
    <div className="relative shrink-0">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] flex items-center justify-center shadow-md ring-2 ring-white text-lg leading-none">
        🤖
      </div>
      <span className="rc-online-dot absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-white" aria-hidden="true" />
    </div>
  );
}






function BotBubble({ children }) {
  return (
    <div className="rc-msg flex items-end gap-2 mb-4 max-w-[88%]">
      <BotAvatar />
      <div className="relative bg-white border border-gray-100 shadow-[0_6px_18px_rgba(124,58,237,0.12)] rounded-[1.4rem] rounded-bl-md px-4 py-2.5 text-sm text-[#3B0764] leading-relaxed break-words">
        {children}
        <span
          className="rc-bubble-tail absolute -left-1.5 bottom-0 w-3 h-3 bg-white border-l border-b border-gray-100"
          style={{ borderBottomLeftRadius: '3px', clipPath: 'polygon(100% 0, 0 100%, 100% 100%)' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

function UserBubble({ children }) {
  return (
    <div className="rc-msg flex justify-end mb-4">
      <div className="relative bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white shadow-[0_8px_20px_rgba(124,58,237,0.35)] rounded-[1.4rem] rounded-br-md px-4 py-2.5 text-sm max-w-[88%] break-words">
        {children}
        <span
          className="rc-bubble-tail absolute -right-1.5 bottom-0 w-3 h-3 bg-[#EC4899]"
          style={{ clipPath: 'polygon(0 0, 100% 100%, 0 100%)' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="rc-msg flex items-end gap-2 mb-4">
      <BotAvatar />
      <div className="bg-white border border-gray-100 shadow-sm rounded-[1.4rem] rounded-bl-md px-4 py-3.5 flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="rc-dot w-2 h-2 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899]"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

/* Job-themed hero illustration for the welcome step — a friendly briefcase
   with a resume peeking out and a "matched" checkmark badge, built as an
   inline SVG so it needs no image asset/import and inherits the brand
   gradient. Purely decorative (aria-hidden). */
function HeroIllustration() {
  return (
    <div className="rc-card flex justify-center mb-1" aria-hidden="true" style={{ animationDelay: '0.05s' }}>
      <div className="relative">
        <svg width="148" height="148" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="rc-hero-case" x1="20" y1="70" x2="180" y2="170" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#7C3AED" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
            <linearGradient id="rc-hero-paper" x1="60" y1="40" x2="140" y2="110" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FDF4FF" />
              <stop offset="100%" stopColor="#F5E9FE" />
            </linearGradient>
          </defs>

          {/* soft ground shadow */}
          <ellipse cx="100" cy="176" rx="58" ry="9" fill="#3B0764" opacity="0.12" />

          {/* resume peeking out behind the case */}
          <rect x="62" y="38" width="76" height="96" rx="10" fill="url(#rc-hero-paper)" stroke="#E9D5FF" strokeWidth="2" transform="rotate(-6 100 86)" />
          <rect x="76" y="56" width="40" height="6" rx="3" fill="#C4B5FD" transform="rotate(-6 100 86)" />
          <rect x="76" y="70" width="48" height="5" rx="2.5" fill="#DDD6FE" transform="rotate(-6 100 86)" />
          <rect x="76" y="80" width="48" height="5" rx="2.5" fill="#DDD6FE" transform="rotate(-6 100 86)" />
          <rect x="76" y="90" width="34" height="5" rx="2.5" fill="#DDD6FE" transform="rotate(-6 100 86)" />

          {/* briefcase handle */}
          <rect x="82" y="52" width="36" height="20" rx="9" fill="none" stroke="url(#rc-hero-case)" strokeWidth="8" />

          {/* briefcase body */}
          <rect x="34" y="72" width="132" height="92" rx="18" fill="url(#rc-hero-case)" />
          <rect x="34" y="104" width="132" height="16" fill="#ffffff" opacity="0.15" />
          <rect x="92" y="100" width="16" height="24" rx="5" fill="#ffffff" opacity="0.9" />

          {/* match / hired checkmark badge */}
          <circle cx="156" cy="70" r="24" fill="#10B981" stroke="#F9FAFB" strokeWidth="5" />
          <path d="M146 70 L153 77 L167 62" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
        <span className="absolute -top-1 -left-3 text-lg rc-heart-beat" style={{ animationDelay: '0.3s' }}>✨</span>
        <span className="absolute bottom-2 -right-2 text-base rc-heart-beat" style={{ animationDelay: '0.7s' }}>🎯</span>
      </div>
    </div>
  );
}

/* Full-bleed gradient "match card" — the Boo-style quick-reply, styled like
   a small swipe card rather than a flat outlined tile. Alternates a gentle
   tilt so a row of cards reads as a loose stack, not a grid. */
function TileCard({ icon, title, subtitle, onClick, tileIndex = 0 }) {
  const style = CARD_STYLES[tileIndex % CARD_STYLES.length];
  const tilt = tileIndex % 2 === 0 ? '-1.5deg' : '1.5deg';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: style.bg, '--rc-tilt': tilt, '--rc-tile-ring': style.ring }}
      className="rc-tile rc-card flex-1 flex flex-col items-center gap-2.5 p-5 rounded-[1.6rem] text-white shadow-[0_10px_26px_-8px_rgba(124,58,237,0.5)]"
    >
      <span className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl ring-1 ring-white/30">
        {icon}
      </span>
      <span className="rc-font-display text-base font-semibold text-center leading-tight">{title}</span>
      {subtitle && <span className="text-[11px] text-white/80 text-center">{subtitle}</span>}
    </button>
  );
}

function PillOption({ label, icon, onClick, tileIndex = 0 }) {
  const style = CARD_STYLES[tileIndex % CARD_STYLES.length];
  return (
    <button
      type="button"
      onClick={onClick}
      className="rc-pill rc-chip-in group flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border-2 border-gray-100 bg-white hover:border-transparent hover:shadow-lg"
      style={{ '--tw-shadow-color': style.ring }}
    >
      {icon ? (
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0"
          style={{ background: style.bg }}
        >
          {icon}
        </span>
      ) : (
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: style.bg }} />
      )}
      <span className="group-hover:text-[#7C3AED]">{label}</span>
    </button>
  );
}

function Chip({ label, onRemove }) {
  return (
    <div className="rc-chip-in bg-[#F5E9FE] text-[#7C3AED] px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 border border-[#7C3AED]/15 shadow-[0_2px_8px_rgba(124,58,237,0.12)]">
      <span>{label}</span>
      {onRemove && <button type="button" onClick={onRemove} className="leading-none opacity-60 hover:opacity-100 hover:scale-125 transition-transform">&times;</button>}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rc-primary-btn rc-font-display w-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-3 rounded-full font-semibold text-sm shadow-[0_6px_18px_rgba(124,58,237,0.35)] hover:shadow-[0_10px_26px_rgba(124,58,237,0.5)] disabled:opacity-50 disabled:shadow-none disabled:translate-y-0"
    >
      {children}
    </button>
  );
}

// Show/hide toggle for password fields — plain inline SVG eye / eye-off so
// no icon library is needed.
function EyeToggleButton({ visible, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#7C3AED] transition-colors"
    >
      {visible ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.86 21.86 0 0 1 5.06-6.06M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.86 21.86 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

function TextComposer({ value, onChange, onSubmit, placeholder, type = 'text', autoFocus }) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="flex items-center gap-2 flex-1 min-w-0"   // 👈 add flex-1 min-w-0
    >
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="flex-1 min-w-0 border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all"   // 👈 add min-w-0 here too
      />
      <button
        type="submit"
        disabled={!value?.toString().trim()}
        className="rc-send-btn w-11 h-11 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center shrink-0 shadow-md hover:scale-110 active:scale-90 transition-transform disabled:opacity-40 disabled:hover:scale-100"
      >
        ➤
      </button>
    </form>
  );
}

function InlineAutocomplete({ options, value, onChange, onSelect, placeholder, loading, hideSubmit = false }) {
  const [open, setOpen] = useState(false);
  const filtered = value?.trim().length > 0
    ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase())).slice(0, 6)
    : [];

  const trimmed = value?.trim() || '';
  // Only offer "use as typed" if it's not already an exact match in the list —
  // no point suggesting a duplicate of something already selectable.
  const exactMatch = filtered.some((f) => f.toLowerCase() === trimmed.toLowerCase());
  const showUseTyped = trimmed.length >= 2 && !exactMatch;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trimmed) return;
    setOpen(false);
    onSelect(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      {open && (filtered.length > 0 || showUseTyped) && (
        <div className="rc-msg absolute z-10 left-0 right-0 bottom-full mb-2 bg-white border border-gray-100 rounded-[1.3rem] shadow-2xl max-h-52 overflow-y-auto p-1.5">
          {filtered.map((f, i) => (
            <button
              key={f}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); setOpen(false); onSelect(f); }}
              style={{ animationDelay: `${i * 35}ms` }}
              className="rc-chip-in w-full text-left px-3.5 py-2.5 text-sm text-gray-700 hover:bg-[#F5E9FE] hover:text-[#7C3AED] rounded-2xl transition flex items-center gap-2"
            >
              <span className="text-xs opacity-50">✦</span>{f}
            </button>
          ))}
          {showUseTyped && (
            <button
              type="button"
              onPointerDown={(e) => { e.preventDefault(); setOpen(false); onSelect(trimmed); }}
              className="rc-chip-in w-full text-left px-3.5 py-2.5 text-sm text-[#7C3AED] font-semibold hover:bg-[#F5E9FE] rounded-2xl transition flex items-center gap-2 border-t border-gray-50 mt-1 pt-2.5"
            >
              <span className="text-xs">＋</span> Use "{trimmed}"
            </button>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          autoFocus
          className="flex-1 border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all"
        />
               {!hideSubmit && (
          <button
            type="submit"
            disabled={!trimmed}
            className="rc-send-btn w-11 h-11 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center shrink-0 shadow-md hover:scale-110 active:scale-90 transition-transform disabled:opacity-40 disabled:hover:scale-100"
          >
            ➤
          </button>
        )}
      </div>
      {loading && (
        <p className="text-xs text-gray-400 mt-1.5 px-1 flex items-center gap-1">
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" />
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" style={{ animationDelay: '150ms' }} />
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" style={{ animationDelay: '300ms' }} />
          Searching…
        </p>
      )}
    </form>
  );
}

// Same as InlineAutocomplete, but never offers "Use <typed text>" — city
// names must come from the suggestion list (static CITIES_LIST + AI), so
// misspellings like "Bangaluru" can't slip into the DB as a distinct value.
function CityOnlyAutocomplete({ options, value, onChange, onSelect, placeholder, loading }) {
  const [open, setOpen] = useState(false);
  const q = value?.trim().toLowerCase() || '';
  const filtered = q
    ? options
        .filter((o) => o.toLowerCase().includes(q))
        .sort((a, b) => Number(b.toLowerCase().startsWith(q)) - Number(a.toLowerCase().startsWith(q)))
        .slice(0, 8)
    : [];

  return (
    <div className="relative">
      {open && filtered.length > 0 && (
        <div className="rc-msg absolute z-10 left-0 right-0 bottom-full mb-2 bg-white border border-gray-100 rounded-[1.3rem] shadow-2xl max-h-52 overflow-y-auto p-1.5">
          {filtered.map((f, i) => (
            <button
              key={f}
              type="button"
              onPointerDown={(e) => { e.preventDefault(); setOpen(false); onSelect(f); }}
              style={{ animationDelay: `${i * 35}ms` }}
              className="rc-chip-in w-full text-left px-3.5 py-2.5 text-sm text-gray-700 hover:bg-[#F5E9FE] hover:text-[#7C3AED] rounded-2xl transition flex items-center gap-2"
            >
              <span className="text-xs opacity-50">✦</span>{f}
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all"
      />
      {loading && (
        <p className="text-xs text-gray-400 mt-1.5 px-1 flex items-center gap-1">
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" />
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" style={{ animationDelay: '150ms' }} />
          <span className="rc-dot w-1 h-1 rounded-full bg-[#7C3AED]" style={{ animationDelay: '300ms' }} />
          Searching…
        </p>
      )}
    </div>
  );
}

/* Segmented "story bar" progress — one pill per step in the flow, so the
   structure itself communicates how many steps remain (à la Boo/IG stories),
   rather than a single anonymous strip. */
function StoryProgress({ total, filled }) {
  return (
    <div className="flex gap-1 px-4 pt-2.5 pb-2 bg-white" role="progressbar" aria-valuenow={filled} aria-valuemin={0} aria-valuemax={total}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          {i < filled && <div className="rc-progress-seg-fill h-full rounded-full" />}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Step order — used only to compute progress-dot position                 */
/* ---------------------------------------------------------------------- */

function stepOrderFor(role, experienceLevel) {
  const base = ['welcome', 'name', 'gender', 'current_location', 'phone', 'otp', 'account', 'role'];
  if (role === 'employer') return [...base, 'employer_company', 'summary'];
  if (experienceLevel === 'experienced') {
    return [...base, 'exp_level', 'current_designation', 'current_salary', 'current_company',
      'employment_period', 'prev_employer_choice', 'target_role', 'locations', 'skills', 'summary'];
  }
  return [...base, 'exp_level', 'looking_role', 'locations', 'skills', 'summary'];
}



/**
 * Reconstructs the chat history from saved form answers, so a resumed
 * registration looks like a normal conversation instead of silently
 * jumping to a step. Only emits a bubble pair for fields that are
 * actually filled in.
 */
function buildResumeMessages(form, uptoStep) {
  const order = stepOrderFor(form.role, form.experienceLevel);
  const idx = order.indexOf(uptoStep);
  const stepsToShow = idx >= 0 ? order.slice(0, idx) : order;

  const msgs = [];
  const push = (bot, user) => {
    if (bot) msgs.push({ id: uid(), from: 'bot', content: bot });
    if (user) msgs.push({ id: uid(), from: 'user', content: user });
  };

  let employersShown = false;

  stepsToShow.forEach((s) => {
    switch (s) {
      case 'name':
        if (form.name) push('What should we call you?', form.name);
        break;
      case 'gender':
        if (form.gender) push('What is your gender?', form.gender);
        break;
              case 'current_location':
        if (form.currentLocation) push('Which city do you currently live in?', form.currentLocation);
        break;

      case 'phone':
        if (form.phone) push("What's your mobile number?", `+91 ${form.phone}`);
        break;
      case 'account':
        if (form.email) push("Let's set up your account login.", form.email);
        break;
      case 'role':
        if (form.role) push('What are you looking for on PlanB Jobs?', form.role === 'job_seeker' ? "I'm looking for a Job" : "I'm hiring");
        break;
      case 'exp_level':
        if (form.experienceLevel) push('Are you a fresher or experienced?', form.experienceLevel === 'fresher' ? 'Fresher' : 'Experienced');
        break;
      case 'looking_role':
        if (form.lookingForRoles?.length) push('What type of role are you looking for?', form.lookingForRoles.join(', '));
        break;
      case 'current_designation':
        if (form.currentDesignation) push('What is your current designation?', form.currentDesignation);
        break;
      case 'current_salary':
        if (form.currentSalary) push('What is your current / last salary?', `₹${form.currentSalary} / ${form.salaryPeriod}`);
        break;
      case 'current_company':
        if (form.currentCompany) push('Which company do you work for?', form.currentCompany);
        break;
      case 'employment_period':
        if (form.startMonth && form.startYear) {
          push('When did you start working here?', `${form.startMonth} ${form.startYear} → ${form.currentlyWorking ? 'Present' : `${form.endMonth} ${form.endYear}`}`);
        }
        break;
      case 'prev_employer_choice':
        if (form.hasPreviousEmployer !== null) {
          push('Do you have previous work experience?', form.hasPreviousEmployer ? 'Yes, add previous employer' : 'No, continue');
        }
        break;
      case 'prev_employer_add':
      case 'prev_employer_more':
        if (!employersShown && form.previousEmployers?.length) {
          employersShown = true;
          form.previousEmployers.forEach((p) => {
            push(null, `${p.designation} at ${p.company}${p.from ? ` (${p.from} – ${p.to || 'Present'})` : ''}`);
          });
        }
        break;
      case 'target_role':
        if (form.targetRoles?.length) push('What role are you looking for next?', form.targetRoles.join(', '));
        break;
      case 'locations':
        if (form.locations?.length) push('Which cities do you prefer?', form.locations.join(', '));
        break;
      case 'skills':
        if (form.skills?.length) push('What skills do you have?', form.skills.join(', '));
        break;
      case 'employer_company':
        if (form.company_industry) push('Tell us about your company.', `${form.company_industry} · ${form.company_size}`);
        break;
      default:
        break;
    }
  });

  return msgs;
}

/* ---------------------------------------------------------------------- */
/* Main component                                                          */
/* ---------------------------------------------------------------------- */

function RegisterChat({ embedded = false, preset = null, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const [prefillPhone] = useState(() => location.state?.prefillPhone || '');

  const [messages, setMessages] = useState([
    { id: uid(), from: 'bot', content: "👋 Hi! Welcome to PlanB Jobs." },
    { id: uid(), from: 'bot', content: "Let's create your profile. It'll only take a few minutes." },
  ]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState('welcome');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '', gender: '', currentLocation: '', phone: '', email: '', password: '', password_confirmation: '',
    role: preset?.role || '', experienceLevel: preset?.experienceLevel || '',
    lookingForRoles: [],
    currentDesignation: '', currentSalary: '', salaryPeriod: 'year', currentCompany: '',
    startMonth: '', startYear: '', currentlyWorking: true, endMonth: '', endYear: '',
    hasPreviousEmployer: null, previousEmployers: [], targetRoles: [],
    locations: [], skills: [],
    company_industry: '', company_size: '',
  });

  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [otpToken, setOtpToken] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);


const [agreedToTerms, setAgreedToTerms] = useState(false);
const [stepHistory, setStepHistory] = useState([]); // now holds { step, msgCount } entries
const stepEntryMsgCountRef = useRef(2); // matches the 2 welcome bot lines set as initial messages


  const [draft, setDraft] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [locInput, setLocInput] = useState('');
  const [currentLocationInput, setCurrentLocationInput] = useState('');
  const [prevDraft, setPrevDraft] = useState({ company: '', designation: '', from: '', to: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // --- AI: company-name suggestions (current_company step) ---------------
  const [companyQuery, setCompanyQuery] = useState('');
  const [aiCompanySuggestions, setAiCompanySuggestions] = useState([]);
  const [aiCompanyLoading, setAiCompanyLoading] = useState(false);
  const companyDebounceRef = useRef(null);

    // --- AI: role/keyword suggestions (looking_role step) ------------------
  const [roleQuery, setRoleQuery] = useState('');
  const [aiRoleSuggestions, setAiRoleSuggestions] = useState([]);
  const [aiRoleLoading, setAiRoleLoading] = useState(false);
  const roleDebounceRef = useRef(null);

  //  const [aiLocationSuggestions, setAiLocationSuggestions] = useState([]);
  // const [aiLocationLoading, setAiLocationLoading] = useState(false);
  // const locationDebounceRef = useRef(null);


  const [masterCities, setMasterCities] = useState([]); // [{ id, name, district, state }]
const [citiesLoading, setCitiesLoading] = useState(true);


  const [industryQuery, setIndustryQuery] = useState('');
const [aiIndustrySuggestions, setAiIndustrySuggestions] = useState([]);
const [aiIndustryLoading, setAiIndustryLoading] = useState(false);
const industryDebounceRef = useRef(null);


  // --- AI: skill suggestions (skills step) --------------------------------
  const [aiSkillSuggestions, setAiSkillSuggestions] = useState([]);
  const [aiSkillsLoading, setAiSkillsLoading] = useState(false);


  const [googleMode, setGoogleMode] = useState(false);

    const [resumeTargetStep, setResumeTargetStep] = useState(null);
  const progressSaveTimer = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);


  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown > 0]);


    const resumeAppliedRef = useRef(false); // guards against StrictMode double-invoke

  useEffect(() => {
    if (resumeAppliedRef.current) return;

    const { resumePhone, resumeOtpToken, resumeStep, resumeData } = location.state || {};
    if (!resumePhone) return;

    resumeAppliedRef.current = true;

    const localSaved = loadProgress(resumePhone);
    const step_ = (resumeStep && !NON_RESUMABLE_STEPS.includes(resumeStep))
      ? resumeStep
      : (localSaved && !NON_RESUMABLE_STEPS.includes(localSaved.step) ? localSaved.step : null);

    const data_ = resumeData || localSaved?.form;
    const targetStep = step_ || 'account';

    const applyRestoredForm = () => {
      if (data_) setForm((f) => ({ ...f, ...data_, phone: resumePhone }));
    };

    if (resumeOtpToken) {
      setOtpToken(resumeOtpToken);
      applyRestoredForm();

      const history = data_ ? buildResumeMessages({ ...data_, phone: resumePhone }, targetStep) : [];
      setMessages([
        ...history,
        { id: uid(), from: 'bot', content: history.length
            ? "Welcome back! Here's everything you already told us — tap Back if you'd like to change anything."
            : "Welcome back! Let's finish setting up your account." },
      ]);
      setStep(targetStep);
    } else {
      set({ phone: resumePhone });
      applyRestoredForm();
      setResumeTargetStep(targetStep);
      say("Welcome back! Let's verify your number again to continue.");
      setDraft(resumePhone);
      setStep('phone');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  if (!prefillPhone || location.state?.resumePhone) return;
  setMessages([
    { id: uid(), from: 'bot', content: "👋 Hi! We couldn't find an account for this number." },
    { id: uid(), from: 'bot', content: "Let's create your profile. It'll only take a few minutes." },
  ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    if (!form.phone) return;
    saveProgress(form.phone, { step, form, stepHistory, messages });
  }, [step, form, stepHistory, messages]);



  useEffect(() => {
    if (!form.phone || !otpToken || NON_RESUMABLE_STEPS.includes(step)) return;
    if (progressSaveTimer.current) clearTimeout(progressSaveTimer.current);
    progressSaveTimer.current = setTimeout(() => {
      api.post('/register/progress', {
        phone: form.phone,
        otp_token: otpToken,
        step,
        data: form,
      }).catch((err) => console.warn('Progress save failed', err.response?.data || err.message));
    }, 800);
    return () => clearTimeout(progressSaveTimer.current);
  }, [step, form, otpToken]);


  useEffect(() => {
  let alive = true;
  api.get('/locations/cities')
    .then((res) => { if (alive) setMasterCities(res.data || []); })
    .catch((err) => console.warn('Could not load cities', err.response?.data || err.message))
    .finally(() => { if (alive) setCitiesLoading(false); });
  return () => { alive = false; };
}, []);

 useEffect(() => {
  return () => {
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    if (roleDebounceRef.current) clearTimeout(roleDebounceRef.current);
    if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);
  };
}, []);

/////////



  // useEffect(() => {
  //   const { resumePhone, resumeOtpToken, resumeStep, resumeData } = location.state || {};
  //   if (!resumePhone) return;

  //   const localSaved = loadProgress(resumePhone);

  //   const step_ = (resumeStep && !NON_RESUMABLE_STEPS.includes(resumeStep))
  //     ? resumeStep
  //     : (localSaved && !NON_RESUMABLE_STEPS.includes(localSaved.step) ? localSaved.step : null);

  //   // Server data (survives cleared browsers / new devices) wins; fall back
  //   // to whatever's cached locally if the server has nothing yet.
  //   const data_ = resumeData || localSaved?.form;

  //   const restoreState = () => {
  //     if (data_) setForm((f) => ({ ...f, ...data_, phone: resumePhone }));
  //     if (localSaved?.stepHistory) setStepHistory(localSaved.stepHistory);
  //     if (localSaved?.messages?.length) setMessages(localSaved.messages);
  //   };

  //   if (resumeOtpToken) {
  //     setOtpToken(resumeOtpToken);
  //     restoreState();
  //     say(step_
  //       ? "Welcome back! Here's everything you already told us — let's continue."
  //       : "Welcome back! Let's finish setting up your account.");
  //     setStep(step_ || 'account');
  //   } else {
  //     set({ phone: resumePhone });
  //     restoreState();
  //     setResumeTargetStep(step_ || 'account');
  //     say("Welcome back! Let's verify your number again to continue.");
  //     setDraft(resumePhone);
  //     setStep('phone');
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);


  ///////

  const say = (content) => setMessages((m) => [...m, { id: uid(), from: 'bot', content }]);
  const reply = (content) => setMessages((m) => [...m, { id: uid(), from: 'user', content }]);

  const goTo = (nextStep, botLines) => {
    setError('');
    const prevStep = step;
    const prevEntryMsgCount = stepEntryMsgCountRef.current;

    const finalize = () => {
      // Remember prevStep AND the message-count snapshot from when we entered
      // prevStep, so goBack can truncate cleanly instead of appending.
      setStepHistory((h) => [...h, { step: prevStep, msgCount: prevEntryMsgCount }]);
      setMessages((m) => {
        stepEntryMsgCountRef.current = m.length; // snapshot after nextStep's bot line(s)
        return m;
      });
      setStep(nextStep);
    };

    const lines = Array.isArray(botLines) ? botLines : botLines ? [botLines] : [];
    if (lines.length === 0) { finalize(); return; }
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      lines.forEach((l) => say(l));
      finalize();
    }, 450);
  };


  const goBack = () => {
    setStepHistory((h) => {
      if (h.length === 0) return h;
      const last = h[h.length - 1];
      setStep(last.step);
      setMessages((m) => m.slice(0, last.msgCount));
      stepEntryMsgCountRef.current = last.msgCount;
      setError('');
      return h.slice(0, -1);
    });
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  /* ---------------- AI: company suggestions -------------------------- */

  const fetchCompanySuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setAiCompanySuggestions([]);
      return;
    }
    setAiCompanyLoading(true);
    try {
      const res = await api.post('/ai/suggest-companies', { query: query.trim() });
      setAiCompanySuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiCompanySuggestions([]);
    } finally {
      setAiCompanyLoading(false);
    }
  };

  // Debounce: wait 350ms after the user stops typing before calling the AI.
  const handleCompanyQueryChange = (value) => {
    setCompanyQuery(value);
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    companyDebounceRef.current = setTimeout(() => {
      fetchCompanySuggestions(value);
    }, 350);
  };


  /* ---------------- AI: role suggestions ------------------------------ */

   const fetchRoleSuggestions = async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setAiRoleSuggestions([]);
      return;
    }
    setAiRoleLoading(true);
    try {
      const res = await api.post('/ai/suggest-keywords', { query: query.trim() });
      setAiRoleSuggestions(res.data.suggestions || []);
    } catch (err) {
      setAiRoleSuggestions([]);
    } finally {
      setAiRoleLoading(false);
    }
  };

    const handleRoleQueryChange = (value) => {
    setRoleQuery(value);
    setAiRoleSuggestions([]); // clear stale suggestions immediately
    if (roleDebounceRef.current) clearTimeout(roleDebounceRef.current);
    roleDebounceRef.current = setTimeout(() => fetchRoleSuggestions(value), 350);
  };


  /* ---------------- AI: industry suggestions -------------------------- */

const fetchIndustrySuggestions = async (query) => {
  if (!query.trim() || query.trim().length < 2) {
    setAiIndustrySuggestions([]);
    return;
  }
  setAiIndustryLoading(true);
  try {
    const res = await api.post('/ai/suggest-industries', { query: query.trim() });
    setAiIndustrySuggestions(res.data.suggestions || []);
  } catch (err) {
    setAiIndustrySuggestions([]);
  } finally {
    setAiIndustryLoading(false);
  }
};

const handleIndustryQueryChange = (value) => {
  setIndustryQuery(value);
  set({ company_industry: value });
  setAiIndustrySuggestions([]);
  if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);
  industryDebounceRef.current = setTimeout(() => fetchIndustrySuggestions(value), 350);
};

const handleGoogleCredential = async (credential) => {
  try {
    const res = await api.post('/auth/google', { credential });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));

    if (!res.data.needs_profile_completion) {
      // Already a complete account — just log them straight in.
      const role = res.data.user.role;
      if (role === 'employer' || role === 'recruiter') navigate('/employer/jobs');
      else if (role === 'agent') navigate('/agent/dashboard');
      else if (role === 'admin') navigate('/admin');
      else navigate('/dashboard');
      return;
    }

    setGoogleMode(true);
    set({ name: res.data.user.name || '', email: res.data.user.email || '' });
    reply('Continue with Google');
    goTo('gender', `Nice to meet you, ${res.data.user.name || 'there'}! What is your gender?`);
  } catch (err) {
    say(err.response?.data?.message || 'Google sign-in failed. Please try again.');
  }
};



   /* ---------------- Master cities ---------------- */

/* ---------------- Master cities ---------------- */

// label -> city name. Label is "City, State"; if two cities would get the same label,
// the district is added ("City, District, State") so each one stays distinct.
const cityLabelMap = useMemo(() => {
  const base = (c) => [c.name, c.state].filter(Boolean).join(', ');
  const counts = {};
  masterCities.forEach((c) => { const b = base(c); counts[b] = (counts[b] || 0) + 1; });

  const map = new Map();
  masterCities.forEach((c) => {
    const b = base(c);
    const label = counts[b] > 1 && c.district
      ? [c.name, c.district, c.state].filter(Boolean).join(', ')
      : b;
    map.set(label, c.name);
  });
  return map;
}, [masterCities]);

const cityOptions = useMemo(() => Array.from(cityLabelMap.keys()), [cityLabelMap]);

// Keep the full label ("City, State") as the saved value; only accept labels from the list
const resolveCity = (label) => (cityLabelMap.has(label) ? label : '');
  /* ---------------- AI: skill suggestions ----------------------------- */

  const fetchSkillSuggestions = async () => {
        const context = form.currentDesignation || form.targetRoles?.[0] || form.lookingForRoles?.[0];
    if (!context) return;
    setAiSkillsLoading(true);
    try {
      const res = await api.post('/ai/suggest-skills', {
        job_title: context,
        company: form.currentCompany,
        existing_skills: [...form.skills, ...aiSkillSuggestions], // exclude picked AND already-suggested
      });
      const fresh = (res.data.suggestions || []).filter(
        (s) => !form.skills.includes(s) && !aiSkillSuggestions.includes(s)
      );
      setAiSkillSuggestions((prev) => [
        ...prev.filter((s) => !form.skills.includes(s)), // keep unpicked leftovers
        ...fresh,
      ]);
    } catch (err) {
      // keep whatever we already had rather than wiping the list on error
    } finally {
      setAiSkillsLoading(false);
    }
  };

useEffect(() => {
  if (step !== 'skills') return;
  if (form.skills.length >= MAX_SKILLS) return; // 👈 add this
  const remaining = aiSkillSuggestions.filter((s) => !form.skills.includes(s)).length;
  if (remaining < SKILL_SUGGESTIONS_VISIBLE) {
    fetchSkillSuggestions();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [step, form.skills]);

  /* ---------------- Step handlers (unchanged logic) ---------------- */

  const handleGetStarted = () => {
    reply('Get Started');
    goTo('name', 'What should we call you?');
  };
  const submitName = () => {
    if (!draft.trim()) return;
    reply(draft); set({ name: draft.trim() }); setDraft('');
    goTo('gender', 'What is your gender?');
  };
  const submitGender = (g) => {
    reply(g); set({ gender: g });
    goTo('current_location', 'Which city do you currently live in?');
  };

const submitCurrentLocation = (v) => {
  const loc = resolveCity(v ?? currentLocationInput);
  if (!loc) { setError('Please select your city from the list.'); return; }
  setError('');
  reply(loc);
  set({ currentLocation: loc });
  setCurrentLocationInput('');
  setDraft(prefillPhone);
  goTo('phone', "What's your mobile number?");
};
const submitPhone = async () => {
  if (!agreedToTerms) { setError('Please accept the Privacy Policy and Terms & Conditions to continue.'); return; }
  if (!/^[6-9]\d{9}$/.test(draft)) { setError('Enter a valid 10-digit mobile number.'); return; }
  reply(`+91 ${draft}`); set({ phone: draft });

  const phone = draft; setDraft(''); setError('');
  try {
    const avail = await api.post('/auth/check-availability', { phone });
    if (avail.data?.phone_registration_complete) {
      setMessages((m) => [
        ...m,
        { id: uid(), from: 'bot', content: 'looks_like_you_already_have_an_account', meta: { type: 'already_registered', phone } },
      ]);
      setStep('already_registered');
      return;
    }
  } catch (err) { console.warn('Availability check failed', err.response?.data || err.message); }

  setSendingOtp(true);
  try {
    await api.post('/otp/send', { phone });
    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setResendCooldown(RESEND_SECONDS);
    goTo('otp', `We've sent a ${OTP_LENGTH}-digit OTP to +91 ${phone}`);
  } catch (err) { say(err.response?.data?.message || "Couldn't send the OTP — please try again."); }
  finally { setSendingOtp(false); }
};
  const handleOtpDigit = (i, v) => {
    const d = v.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits]; next[i] = d; setOtpDigits(next);
    if (d && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  // const submitOtp = async () => {
  //   const otp = otpDigits.join('');
  //   if (otp.length !== OTP_LENGTH) { setError(`Enter the ${OTP_LENGTH}-digit OTP.`); return; }
  //   setVerifyingOtp(true); setError('');
  //   try {
  //     const res = await api.post('/otp/verify', { phone: form.phone, otp });
  //     setOtpToken(res.data.otp_token || res.data.token || '');
  //     reply(otp.split('').join(' '));

  //     const target = resumeTargetStep || 'account';

  //     if (resumeTargetStep && resumeTargetStep !== 'account') {
  //       const history = buildResumeMessages(form, target);
  //       setMessages((m) => [
  //         ...m,
  //         ...history,
  //         { id: uid(), from: 'bot', content: "✅ Verified! Here's everything you already told us — tap Back if you'd like to change anything." },
  //       ]);
  //       setStep(target);
  //     } else {
  //       goTo(target, ['✅ Mobile number verified!', "Now let's set up your account login — email and a password."]);
  //     }
  //     setResumeTargetStep(null);
  //   } catch (err) { setError(err.response?.data?.message || 'Incorrect OTP. Please try again.'); }
  //   finally { setVerifyingOtp(false); }
  // };



  const submitOtp = async () => {
  const otp = otpDigits.join('');
  if (otp.length !== OTP_LENGTH) { setError(`Enter the ${OTP_LENGTH}-digit OTP.`); return; }
  setVerifyingOtp(true); setError('');
  try {
    const res = await api.post('/otp/verify', { phone: form.phone, otp });
    setOtpToken(res.data.otp_token || res.data.token || '');
    reply(otp.split('').join(' '));

    const target = resumeTargetStep || 'account';

    if (resumeTargetStep && resumeTargetStep !== 'account') {
      const history = buildResumeMessages(form, target);
      setMessages((m) => [
        ...m,
        ...history,
        { id: uid(), from: 'bot', content: "✅ Verified! Here's everything you already told us — tap Back if you'd like to change anything." },
      ]);
      setStep(target);
    } else {
      goTo(target, ['✅ Mobile number verified!', "Now let's set up your account login — email and a password."]);
    }
    setResumeTargetStep(null);
  } catch (err) { setError(err.response?.data?.message || 'Incorrect OTP. Please try again.'); }
  finally { setVerifyingOtp(false); }
};

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || sendingOtp) return;
    setSendingOtp(true);
    try {
      await api.post('/otp/send', { phone: form.phone });
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setResendCooldown(RESEND_SECONDS);
      otpRefs.current[0]?.focus();
    } catch (err) { setError(err.response?.data?.message || 'Could not resend OTP.'); }
    finally { setSendingOtp(false); }
  };
 const submitAccount = async () => {
  if (!googleMode) {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailValid) return setError('Enter a valid email address.');
  }
  if (form.password.length < 8) return setError('Password must be at least 8 characters.');
  // if (form.password !== form.password_confirmation) return setError('Passwords do not match.');
  setError('');
  if (!googleMode) {
    try {
      const avail = await api.post('/auth/check-availability', { email: form.email });
      if (avail.data?.email_exists) { setError('An account with this email already exists. Please login instead.'); return; }
    } catch (err) { console.warn('Email availability check failed', err.response?.data || err.message); }
  }
  reply(googleMode ? '••••••••' : form.email);
  if (preset?.role === 'employer') {
    goTo('employer_company', 'Great — a couple of quick details about your company.');
  } else if (preset?.role === 'job_seeker' && preset.experienceLevel === 'fresher') {
    goTo('looking_role', ["Great! Let's build your job profile.", 'What type of role are you looking for?']);
  } else if (preset?.role === 'job_seeker' && preset.experienceLevel === 'experienced') {
    goTo('current_designation', ["Great! Let's build your job profile.", 'What is your current / most recent designation?']);
  } else if (preset?.role === 'job_seeker') {
    goTo('exp_level', ["Great! Let's build your job profile.", 'Are you a fresher or do you have work experience?']);
  } else {
    goTo('role', 'What are you looking for on PlanB Jobs?');
  }
};
  const submitRole = (role) => {
    reply(role === 'job_seeker' ? "I'm looking for a Job" : "I'm hiring");
    set({ role });
    if (role === 'employer') goTo('employer_company', "Great — a couple of quick details about your company.");
    else goTo('exp_level', ["Great! Let's build your job profile.", 'Are you a fresher or do you have work experience?']);
  };
  const submitExpLevel = (level) => {
    reply(level === 'fresher' ? 'Fresher' : 'Experienced');
    set({ experienceLevel: level });
    if (level === 'fresher') goTo('looking_role', 'What type of role are you looking for?');
    else goTo('current_designation', 'What is your current / most recent designation?');
  };
  const addLookingRole = (v) => {
    if (!v.trim() || form.lookingForRoles.includes(v.trim()) || form.lookingForRoles.length >= MAX_LOOKING_ROLES) return;
    set({ lookingForRoles: [...form.lookingForRoles, v.trim()] });
    setRoleQuery(''); setAiRoleSuggestions([]);
  };
  const removeLookingRole = (i) => set({ lookingForRoles: form.lookingForRoles.filter((_, idx) => idx !== i) });
  const submitLookingRoles = () => {
    if (form.lookingForRoles.length === 0) { setError('Add at least one role.'); return; }
    setError(''); reply(form.lookingForRoles.join(', '));
    goTo('locations', [`Where would you like to work?`, `Please add at least 3 cities (up to ${MAX_LOCATIONS}).`]);
  };
    const submitCurrentDesignation = (v) => {
    reply(v); set({ currentDesignation: v });
    setRoleQuery(''); setAiRoleSuggestions([]);
    goTo('current_salary', 'What is your current / last salary?');
  };
  const submitCurrentSalary = () => {
    if (!draft.trim()) return;
    reply(`₹${draft} / ${form.salaryPeriod === 'year' ? 'year' : 'month'}`);
    set({ currentSalary: draft.trim() }); setDraft('');
    goTo('current_company', 'Which company do you currently work for?');
  };
  const submitCurrentCompany = (v) => {
    reply(v); set({ currentCompany: v });
    setCompanyQuery(''); setAiCompanySuggestions([]);
    goTo('employment_period', 'When did you start working here?');
  };
  const submitEmploymentPeriod = () => {
    if (!form.startMonth || !form.startYear) { setError('Please add a start month and year.'); return; }
    if (!form.currentlyWorking && (!form.endMonth || !form.endYear)) { setError('Please add an end month and year, or mark this as your current job.'); return; }
    setError('');
    const label = `${form.startMonth} ${form.startYear} → ${form.currentlyWorking ? 'Present' : `${form.endMonth} ${form.endYear}`}`;
    reply(label);
    goTo('prev_employer_choice', 'Do you have previous work experience?');
  };
   const submitPrevChoice = (yes) => {
    reply(yes ? 'Yes, add previous employer' : 'No, continue');
    set({ hasPreviousEmployer: yes });
    if (yes) {
      setPrevDraft({ company: '', designation: '', from: '', to: '' });
      setCompanyQuery(''); setAiCompanySuggestions([]);   // 👈 add
      setRoleQuery(''); setAiRoleSuggestions([]);          // 👈 add
      goTo('prev_employer_add', `Previous employer ${form.previousEmployers.length + 1}`);
    }
    else goTo('target_role', 'What type of role are you looking for next?');
  };
    const submitPrevEmployer = () => {
    const { company, designation, from, to } = prevDraft;
    if (!company.trim() || !designation.trim()) { setError('Add at least a company name and designation.'); return; }
    setError('');
    const entry = { company: company.trim(), designation: designation.trim(), from: from.trim(), to: to.trim() };
    set({ previousEmployers: [...form.previousEmployers, entry] });
    reply(`${entry.designation} at ${entry.company}${entry.from ? ` (${entry.from} – ${entry.to || 'Present'})` : ''}`);
    setCompanyQuery(''); setAiCompanySuggestions([]);   // 👈 add
    setRoleQuery(''); setAiRoleSuggestions([]);          // 👈 add
    if (form.previousEmployers.length + 1 >= MAX_PREVIOUS_EMPLOYERS) {
      goTo('target_role', ["That's the max for now — you can add more later from your profile.", 'What type of role are you looking for next?']);
    } else {
      goTo('prev_employer_more', 'Add another previous employer?');
    }
  };
   const submitPrevMore = (yes) => {
    reply(yes ? '+ Add another' : 'Continue');
    if (yes) {
      setPrevDraft({ company: '', designation: '', from: '', to: '' });
      setCompanyQuery(''); setAiCompanySuggestions([]);   // 👈 add
      setRoleQuery(''); setAiRoleSuggestions([]);          // 👈 add
      goTo('prev_employer_add', `Previous employer ${form.previousEmployers.length + 1}`);
    }
    else goTo('target_role', 'What type of role are you looking for next?');
  };
     const addTargetRole = (v) => {
    if (!v.trim() || form.targetRoles.includes(v.trim()) || form.targetRoles.length >= MAX_TARGET_ROLES) return;
    set({ targetRoles: [...form.targetRoles, v.trim()] });
    setRoleQuery(''); setAiRoleSuggestions([]);
  };
  const removeTargetRole = (i) => set({ targetRoles: form.targetRoles.filter((_, idx) => idx !== i) });
  const submitTargetRoles = () => {
    if (form.targetRoles.length === 0) { setError('Add at least one role.'); return; }
    setError(''); reply(form.targetRoles.join(', '));
    goTo('locations', [`Which cities would you prefer for your next job?`, `Please select at least 3 cities (up to ${MAX_LOCATIONS}).`]);
  };
const addLocation = (v) => {
  const clean = resolveCity(v);
  if (!clean || form.locations.length >= MAX_LOCATIONS) return;
  const alreadyAdded = form.locations.some((l) => l.toLowerCase() === clean.toLowerCase());
  if (alreadyAdded) { setLocInput(''); return; }
  set({ locations: [...form.locations, clean] }); setLocInput('');
};
  const removeLocation = (i) => set({ locations: form.locations.filter((_, idx) => idx !== i) });
  const submitLocations = () => {
    if (form.locations.length < 3) { setError('Please add at least 3 cities.'); return; }
    setError(''); reply(form.locations.join(', '));
    goTo('skills', form.role === 'job_seeker' ? 'What skills do you have?' : undefined);
  };
 const addSkill = (v) => {
  if (!v.trim() || form.skills.includes(v.trim()) || form.skills.length >= MAX_SKILLS) return; // 👈 add limit check
  set({ skills: [...form.skills, v.trim()] }); setSkillInput('');
};
  const removeSkill = (i) => set({ skills: form.skills.filter((_, idx) => idx !== i) });
  const submitSkills = () => {
    if (form.skills.length === 0) { setError('Add at least one skill.'); return; }
    setError(''); reply(form.skills.join(', '));
    goTo('summary');
  };
  const submitEmployerCompany = () => {
    if (!form.company_industry.trim() || !form.company_size) { setError('Add your industry and company size.'); return; }
    setError(''); reply(`${form.company_industry} · ${form.company_size}`);
    goTo('summary');
  };

 const completeProfile = async () => {
  setSubmitting(true); setError('');
  try {
    const attribution = getStoredAttribution();

    const res = googleMode
      ? await api.post('/auth/google/complete', {
          password: form.password,
          // password_confirmation: form.password_confirmation,
          password_confirmation: form.password,
          phone: form.phone,
          role: form.role,
          otp_token: otpToken,
          ...(attribution ? { attribution } : {}),
        })
      : await api.post('/register', {
          name: form.name, email: form.email, password: form.password, password_confirmation: form.password,
          phone: form.phone, role: form.role, otp_token: otpToken, otp_verified_via: 'phone',
          ...(attribution ? { attribution } : {}),
        });

    if (!googleMode) localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    clearProgress(form.phone);

    // ...everything after this line stays exactly as it is today...

     try {
  await api.put('/profile', {
    name: form.name,
    gender: form.gender ? form.gender.toLowerCase() : '',
    city: form.currentLocation || form.locations[0] || '',
    preferred_locations: form.locations,
    preferred_job_designations: form.experienceLevel === 'fresher'
      ? form.lookingForRoles
      : form.targetRoles,
    ...(form.experienceLevel === 'experienced' ? {
      current_company: form.currentCompany,
      current_designation: form.currentDesignation,
    } : {}),
    career_objective: [
      form.experienceLevel === 'fresher' ? `Looking for: ${form.lookingForRoles.join(', ')}` : `Looking for: ${form.targetRoles.join(', ')}`,
      form.locations.length ? `Preferred locations: ${form.locations.join(', ')}` : '',
    ].filter(Boolean).join(' — '),
  });
} catch (e) { console.warn('Profile save failed', e.response?.data || e.message); }

      await Promise.allSettled(form.skills.map((skill_name) => api.post('/skills', { skill_name, proficiency: 'intermediate' })));

      if (form.experienceLevel === 'experienced') {
        const startDate = form.startYear && form.startMonth ? `${form.startYear}-${String(MONTHS.indexOf(form.startMonth) + 1).padStart(2, '0')}-01` : null;
        const endDate = !form.currentlyWorking && form.endYear && form.endMonth
          ? `${form.endYear}-${String(MONTHS.indexOf(form.endMonth) + 1).padStart(2, '0')}-01` : null;
        const savers = [
          api.post('/experiences', {
            job_title: form.currentDesignation, company_name: form.currentCompany,
            start_date: startDate, end_date: endDate, currently_working: form.currentlyWorking,
            description: form.currentSalary ? `Salary: ₹${form.currentSalary}/${form.salaryPeriod}` : '',
          }),
          ...form.previousEmployers.map((p) => api.post('/experiences', {
            job_title: p.designation, company_name: p.company, start_date: p.from || null,
            end_date: p.to || null, currently_working: false, description: '',
          })),
        ];
        await Promise.allSettled(savers);
      }

      const role = res.data.user?.role || form.role;
      if (role === 'employer' || role === 'recruiter') navigate('/employer/jobs');
      else if (role === 'agent') navigate('/agent/dashboard');
      else if (role === 'admin') navigate('/admin');
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Composer                                                                 */
  /* ---------------------------------------------------------------------- */

  const composer = () => {
    switch (step) {
     case 'welcome':
  return (
    <div>
      <HeroIllustration />
      <PrimaryButton onClick={handleGetStarted}>Get Started <span className="rc-heart-beat">→</span></PrimaryButton>
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">OR</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      <GoogleSignInButton onCredential={handleGoogleCredential} onError={say} />
    </div>
  );
      case 'name':
        return <TextComposer value={draft} onChange={setDraft} onSubmit={submitName} placeholder="Enter your full name" autoFocus />;

      case 'gender':
        return (
          <div className="flex flex-wrap gap-2">
            {['Male', 'Female', 'Other', 'Prefer not to say'].map((g, i) => (
              <PillOption key={g} label={g} icon={GENDER_ICONS[g]} tileIndex={i} onClick={() => submitGender(g)} />
            ))}
          </div>
        );

case 'current_location':
  return (
    <CityOnlyAutocomplete
      options={cityOptions}
      value={currentLocationInput}
      onChange={setCurrentLocationInput}
      onSelect={(v) => submitCurrentLocation(v)}
      placeholder="e.g. Bengaluru"
      loading={citiesLoading}
    />
  );

      

      case 'phone':
  return (
    <div>
        <label className="flex items-start gap-2.5 mb-3 px-1 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#7C3AED] shrink-0"
        />
        <span className="text-xs text-gray-500 leading-relaxed">
          I agree to PlanB Jobs collecting and using my name and mobile number for
          registration, verification, communication, and job-related services. I agree to the{' '}
          <Link to="/privacy-policy" target="_blank" className="text-[#7C3AED] font-semibold hover:underline">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms" target="_blank" className="text-[#7C3AED] font-semibold hover:underline">
            Terms &amp; Conditions
          </Link>
          .
        </span>
      </label>

     <div className="flex items-center gap-2">
        <span className="px-3 py-2.5 text-sm text-gray-500 bg-gray-50 border-2 border-gray-100 rounded-full shrink-0">+91</span>
        <TextComposer
          value={draft}
          onChange={(v) => setDraft(v.replace(/\D/g, '').slice(0, 10))}
          onSubmit={submitPhone}
          placeholder="98765 43210"
          type="tel"
          autoFocus
        />
      </div>
      {sendingOtp && <p className="text-xs text-gray-400 mt-1.5 px-1">Sending…</p>}
    </div>
  );

      case 'otp':
        return (
          <div>
            <div className="flex gap-2 sm:gap-2.5 justify-center mb-3">
              {otpDigits.map((d, i) => (
                <input
                  key={i} ref={(el) => (otpRefs.current[i] = el)} type="tel" inputMode="numeric" maxLength={1}
                  value={d} onChange={(e) => handleOtpDigit(i, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                  className={`rc-font-display w-11 h-12 sm:w-12 sm:h-13 py-2.5 text-center text-xl font-bold border-2 rounded-2xl focus:outline-none focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/15 bg-white transition-all ${d ? 'rc-otp-filled border-[#7C3AED] text-[#7C3AED]' : 'border-gray-200'}`}
                />
              ))}
            </div>
            <div className="text-center text-xs text-gray-500 mb-3">
              {resendCooldown > 0 ? <span>Didn't receive OTP? Resend in {resendCooldown}s</span> :
                <button type="button" onClick={handleResendOtp} className="text-[#7C3AED] font-semibold hover:underline">Resend OTP</button>}
            </div>
            <PrimaryButton onClick={submitOtp} disabled={verifyingOtp}>{verifyingOtp ? 'Verifying…' : 'Verify OTP'}</PrimaryButton>
          </div>
        );

    case 'account':
  return (
    <div className="space-y-2.5">
      {!googleMode ? (
       <input type="email" value={form.email}
  onChange={(e) => set({ email: e.target.value.toLowerCase().replace(/\s/g, '') })}
  autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="email"
  placeholder="you@example.com"
  className="w-full border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
      ) : (
        <div className="w-full border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm bg-gray-50 text-gray-500">
          {form.email} <span className="text-xs text-[#7C3AED] font-semibold">(from Google)</span>
        </div>
      )}
      <div className="relative">
        <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => set({ password: e.target.value })} placeholder="Password (min 8 characters)"
          className="w-full border-2 border-gray-100 rounded-full pl-4 pr-11 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
        <EyeToggleButton visible={showPassword} onClick={() => setShowPassword((v) => !v)} />
      </div>
      {/* <div className="relative">
        <input type={showPasswordConfirm ? 'text' : 'password'} value={form.password_confirmation} onChange={(e) => set({ password_confirmation: e.target.value })} placeholder="Confirm password"
          className="w-full border-2 border-gray-100 rounded-full pl-4 pr-11 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
        <EyeToggleButton visible={showPasswordConfirm} onClick={() => setShowPasswordConfirm((v) => !v)} />
      </div> */}
      <PrimaryButton onClick={submitAccount}>Continue</PrimaryButton>
    </div>
  );

      case 'role':
        return (
<div className="flex flex-col sm:flex-row gap-3">
  <TileCard icon="👤" title="I'm looking for a Job" subtitle="Find jobs & get hired" tileIndex={0} onClick={() => submitRole('job_seeker')} />
  <TileCard icon="🏢" title="I'm hiring" subtitle="Find candidates & hire" tileIndex={1} onClick={() => submitRole('employer')} />
</div>
        );

      case 'exp_level':
        return (
<div className="flex flex-col sm:flex-row gap-3">
  <TileCard icon="🌱" title="Fresher" tileIndex={2} onClick={() => submitExpLevel('fresher')} />
  <TileCard icon="💼" title="Experienced" tileIndex={0} onClick={() => submitExpLevel('experienced')} />
</div>
        );

             case 'looking_role': {
        return (
          <div>
            {form.lookingForRoles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">{form.lookingForRoles.map((r, i) => <Chip key={r} label={r} onRemove={() => removeLookingRole(i)} />)}</div>
            )}
            <p className="text-xs text-gray-400 mb-2 px-1">{form.lookingForRoles.length}/{MAX_LOOKING_ROLES} roles added</p>
            {form.lookingForRoles.length < MAX_LOOKING_ROLES && (
              <InlineAutocomplete
                options={aiRoleSuggestions}
                value={roleQuery}
                onChange={handleRoleQueryChange}
                onSelect={addLookingRole}
                placeholder="Search for a role (e.g. Software Developer)"
                loading={aiRoleLoading}
              />
            )}
            <div className="mt-3"><PrimaryButton onClick={submitLookingRoles}>Continue</PrimaryButton></div>
          </div>
        );
      }

      case 'current_designation': {
        return (
          <InlineAutocomplete
            options={aiRoleSuggestions}
            value={roleQuery}
            onChange={handleRoleQueryChange}
            onSelect={(v) => { setRoleQuery(''); submitCurrentDesignation(v); }}
            placeholder="e.g. Software Developer"
            loading={aiRoleLoading}
          />
        );
      }


           case 'current_salary':
        return (
          <div>
            <div className="flex gap-2 mb-2.5">
              {['month', 'year'].map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => set({ salaryPeriod: period })}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
                    form.salaryPeriod === period
                      ? 'bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white border-transparent shadow-md'
                      : 'bg-white text-gray-500 border-gray-100'
                  }`}
                >
                  Per {period}
                </button>
              ))}
            </div>
           <div className="flex items-center gap-2 min-w-0">
  <span className="px-3 py-2.5 text-sm text-gray-500 bg-gray-50 border-2 border-gray-100 rounded-full shrink-0">₹</span>
  <input type="number" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Amount"
    className="flex-1 min-w-0 border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
  <button type="button" onClick={submitCurrentSalary} disabled={!draft.trim()}
    className="rc-send-btn w-11 h-11 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center shrink-0 shadow-md hover:scale-110 active:scale-90 transition-transform disabled:opacity-40 disabled:hover:scale-100">➤</button>
</div>
          </div>
        );

      case 'current_company':
        // AI-assisted company autocomplete (restored from old Register.jsx —
        // /ai/suggest-companies, debounced 350ms after typing stops).
        return (
          <InlineAutocomplete
            options={aiCompanySuggestions}
            value={companyQuery}
            onChange={handleCompanyQueryChange}
            onSelect={(v) => submitCurrentCompany(v)}
            placeholder="Type at least 2 letters to search companies"
            loading={aiCompanyLoading}
          />
        );

      case 'employment_period':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select value={form.startMonth} onChange={(e) => set({ startMonth: e.target.value })} className="border-2 border-gray-100 rounded-full px-3 py-2.5 text-sm bg-white">
                <option value="">Start month</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={form.startYear} onChange={(e) => set({ startYear: e.target.value })} className="border-2 border-gray-100 rounded-full px-3 py-2.5 text-sm bg-white">
                <option value="">Start year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 px-1">
              <input type="checkbox" checked={form.currentlyWorking} onChange={(e) => set({ currentlyWorking: e.target.checked })} className="accent-[#7C3AED]" />
              I currently work here
            </label>
            {!form.currentlyWorking && (
              <div className="grid grid-cols-2 gap-2">
                <select value={form.endMonth} onChange={(e) => set({ endMonth: e.target.value })} className="border-2 border-gray-100 rounded-full px-3 py-2.5 text-sm bg-white">
                  <option value="">End month</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={form.endYear} onChange={(e) => set({ endYear: e.target.value })} className="border-2 border-gray-100 rounded-full px-3 py-2.5 text-sm bg-white">
                  <option value="">End year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            <PrimaryButton onClick={submitEmploymentPeriod}>Continue</PrimaryButton>
          </div>
        );

      case 'prev_employer_choice':
        return (
          <div className="flex gap-3">
            <button type="button" onClick={() => submitPrevChoice(true)} className="rc-pill flex-1 border-2 border-[#7C3AED] text-[#7C3AED] py-2.5 rounded-full font-semibold text-sm hover:bg-[#F5E9FE]">Yes, add previous employer</button>
            <button type="button" onClick={() => submitPrevChoice(false)} className="rc-primary-btn flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-2.5 rounded-full font-semibold text-sm shadow-md hover:shadow-lg">No, continue</button>
          </div>
        );

          case 'prev_employer_add':
        return (
          <div className="space-y-2.5">
            <InlineAutocomplete
              options={aiCompanySuggestions}
              value={companyQuery || prevDraft.company}
              onChange={(v) => { handleCompanyQueryChange(v); setPrevDraft((p) => ({ ...p, company: v })); }}
              onSelect={(v) => { setPrevDraft((p) => ({ ...p, company: v })); setCompanyQuery(''); setAiCompanySuggestions([]); }}
              placeholder="Company"
              loading={aiCompanyLoading}
            />
            <InlineAutocomplete
              options={aiRoleSuggestions}
              value={roleQuery || prevDraft.designation}
              onChange={(v) => { handleRoleQueryChange(v); setPrevDraft((p) => ({ ...p, designation: v })); }}
              onSelect={(v) => { setPrevDraft((p) => ({ ...p, designation: v })); setRoleQuery(''); setAiRoleSuggestions([]); }}
              placeholder="Designation"
              loading={aiRoleLoading}
            />
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={prevDraft.from} onChange={(e) => setPrevDraft((p) => ({ ...p, from: e.target.value }))} placeholder="From (e.g. 2020)"
                className="border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
              <input type="text" value={prevDraft.to} onChange={(e) => setPrevDraft((p) => ({ ...p, to: e.target.value }))} placeholder="To (e.g. 2022)"
                className="border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-[#7C3AED]/15 focus:border-[#7C3AED] bg-white transition-all" />
            </div>
            <PrimaryButton onClick={submitPrevEmployer}>Add</PrimaryButton>
          </div>
        );

      case 'prev_employer_more':
        return (
          <div className="flex gap-3">
            <button type="button" onClick={() => submitPrevMore(true)} className="rc-pill flex-1 border-2 border-[#7C3AED] text-[#7C3AED] py-2.5 rounded-full font-semibold text-sm hover:bg-[#F5E9FE]">+ Add another</button>
            <button type="button" onClick={() => submitPrevMore(false)} className="rc-primary-btn flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-2.5 rounded-full font-semibold text-sm shadow-md hover:shadow-lg">Continue</button>
          </div>
        );

           case 'target_role': {
        return (
          <div>
            {form.targetRoles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">{form.targetRoles.map((r, i) => <Chip key={r} label={r} onRemove={() => removeTargetRole(i)} />)}</div>
            )}
            <p className="text-xs text-gray-400 mb-2 px-1">{form.targetRoles.length}/{MAX_TARGET_ROLES} roles added</p>
            {form.targetRoles.length < MAX_TARGET_ROLES && (
              <InlineAutocomplete
                options={aiRoleSuggestions}
                value={roleQuery}
                onChange={handleRoleQueryChange}
                onSelect={addTargetRole}
                placeholder="e.g. Software Developer"
                loading={aiRoleLoading}
              />
            )}
            <div className="mt-3"><PrimaryButton onClick={submitTargetRoles}>Continue</PrimaryButton></div>
          </div>
        );
      }
          case 'locations':
  return (
    <div>
      {form.locations.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">{form.locations.map((l, i) => <Chip key={l} label={l} onRemove={() => removeLocation(i)} />)}</div>
      )}
      <p className="text-xs text-gray-400 mb-2 px-1">{form.locations.length}/{MAX_LOCATIONS} locations selected (minimum 3)</p>
      {form.locations.length < MAX_LOCATIONS && (
        <CityOnlyAutocomplete
          options={cityOptions}
          value={locInput}
          onChange={setLocInput}
          onSelect={(v) => addLocation(v)}
          placeholder="Add a city"
          loading={citiesLoading}
        />
      )}
      <div className="mt-3"><PrimaryButton onClick={submitLocations}>Continue</PrimaryButton></div>
    </div>
  );

case 'skills':
  return (
    <div>
      {form.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">{form.skills.map((s, i) => <Chip key={s} label={s} onRemove={() => removeSkill(i)} />)}</div>
      )}
      <p className="text-xs text-gray-400 mb-2 px-1">{form.skills.length}/{MAX_SKILLS} skills added</p>

      {form.skills.length < MAX_SKILLS && (
        <InlineAutocomplete options={SKILLS_LIST} value={skillInput} onChange={setSkillInput} onSelect={addSkill} placeholder="Type a skill and select" />
      )}

      {form.skills.length < MAX_SKILLS && (
        <div className="mt-3 bg-white border border-gray-100 rounded-[1.3rem] p-3.5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="text-[#10B981] text-sm rc-heart-beat">✨</span>
            <span className="rc-font-display text-xs font-semibold text-[#10B981]">AI suggested skills</span>
          </div>
          {aiSkillsLoading ? (
            <p className="text-xs text-gray-400">
              Thinking of skills for "{form.currentDesignation || form.targetRoles?.[0] || form.lookingForRoles?.[0] || 'your role'}"...
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {aiSkillSuggestions
                .filter((s) => !form.skills.includes(s))
                .slice(0, SKILL_SUGGESTIONS_VISIBLE)
                .map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addSkill(s)}
                    className="rc-pill flex items-center gap-1.5 bg-[#ECFDF5] border border-[#10B981]/20 text-[#3B0764] text-xs font-medium px-3 py-1.5 rounded-full hover:border-[#10B981]"
                  >
                    {s} <span className="text-[#10B981]">+</span>
                  </button>
                ))}
              {aiSkillSuggestions.filter((s) => !form.skills.includes(s)).length === 0 && (
                <p className="text-xs text-gray-400">
                  No suggestions yet — add a role first, or type your own skills above.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3"><PrimaryButton onClick={submitSkills}>Continue</PrimaryButton></div>
    </div>
  );

    case 'employer_company':
  return (
    <div className="space-y-2.5">
      <InlineAutocomplete
        options={aiIndustrySuggestions}
        value={industryQuery || form.company_industry}
        onChange={handleIndustryQueryChange}
        onSelect={(v) => { set({ company_industry: v }); setIndustryQuery(''); setAiIndustrySuggestions([]); }}
        placeholder="Industry (e.g. IT Services)"
        loading={aiIndustryLoading}
        hideSubmit
      />
      <select value={form.company_size} onChange={(e) => set({ company_size: e.target.value })} className="w-full border-2 border-gray-100 rounded-full px-4 py-2.5 text-sm bg-white">
        <option value="">Company size</option>
        {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <PrimaryButton onClick={submitEmployerCompany}>Continue</PrimaryButton>
    </div>
  );

      case 'summary': {
        const rows = form.role === 'employer'
          ? [['Name', form.name], ['Industry', form.company_industry], ['Company size', form.company_size]]
          : [
              ['Name', form.name], ['Gender', form.gender], ['Current location', form.currentLocation],
              ['Experience', form.experienceLevel === 'fresher' ? 'Fresher' : 'Experienced'],
              ...(form.experienceLevel === 'fresher'
                ? [['Looking for', form.lookingForRoles.join(', ')]]
                : [['Current', `${form.currentDesignation} · ${form.currentCompany}`], ['Looking for', form.targetRoles.join(', ')]]),
              ['Preferred locations', form.locations.join(', ')],
              ['Skills', form.skills.join(', ')],
            ];
        return (
          <div className="relative">
            {/* One-time confetti burst celebrating the completed profile — purely decorative */}
            <div className="absolute inset-x-0 -top-3 h-0 overflow-visible pointer-events-none" aria-hidden="true">
              {CONFETTI.map((c, i) => (
                <span
                  key={i}
                  className="rc-confetti-piece"
                  style={{ left: c.left, fontSize: c.size, animationDelay: c.delay, animationDuration: c.dur }}
                >
                  {c.glyph}
                </span>
              ))}
            </div>
           <div className="rc-msg rounded-[1.4rem] mb-4 bg-white shadow-[0_8px_24px_rgba(124,58,237,0.12)] border border-gray-100 divide-y divide-gray-50 max-h-[38vh] overflow-y-auto">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between px-4 py-2.5 text-sm gap-3">
            <span className="text-gray-500 shrink-0">{label}</span>
            <span className="font-semibold text-[#3B0764] text-right">{value || '-'}</span>
          </div>
        ))}
      </div>

      {/* button now always sits right after the (capped) scroll box — never pushed off-screen */}
      <PrimaryButton onClick={completeProfile} disabled={submitting}>
        {submitting ? 'Creating account…' : <>🎉 Complete Profile</>}
      </PrimaryButton>
    </div>
  );
      }
      case 'already_registered':
        return (
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setDraft('');
              }}
              className="text-xs font-semibold text-gray-400 hover:text-[#7C3AED]"
            >
              Try a different number
            </button>
          </div>
        );

      default:
        return null;
    }
  };


  const order = stepOrderFor(form.role, form.experienceLevel);
  const idx = Math.max(order.indexOf(step), 0);
  const progressPct = Math.min(100, Math.round(((idx + 1) / order.length) * 100));

  /* ---------------------------------------------------------------------- */

  return (
    <div className={embedded ? 'w-full' : 'min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#3B0764] p-0 sm:py-6 sm:px-3'}>
      <GlobalStyles />

          {!embedded && (
        <>
          {/* Decorative gradient backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#3B0764] via-[#7C3AED] to-[#DB2777]" />
          <div className="rc-blob absolute -top-32 -left-24 w-64 h-64 sm:w-96 sm:h-96 bg-[#EC4899]/25 rounded-full blur-[100px]" />
          <div className="rc-blob absolute -bottom-32 -right-16 w-64 h-64 sm:w-96 sm:h-96 bg-[#7C3AED]/25 rounded-full blur-[100px]" style={{ animationDelay: '2s' }} />
          <div className="rc-blob absolute top-1/3 right-1/4 w-48 h-48 sm:w-72 sm:h-72 bg-[#C026D3]/20 rounded-full blur-[90px]" style={{ animationDelay: '4s' }} />

          {/* Floating decorative glyphs — purely cosmetic, aria-hidden */}
          <div aria-hidden="true" className="hidden sm:block">
            {FLOATERS.map((f, i) => (
              <span
                key={i}
                className="rc-floater"
                style={{ top: f.top, left: f.left, fontSize: f.size, animationDelay: f.delay, animationDuration: f.dur }}
              >
                {f.glyph}
              </span>
            ))}
          </div>
        </>
      )}

      {/* "Device" card */}

      {/* "Device" card */}
      <div className={embedded
        ? 'rc-card relative w-full h-[520px] bg-[#F9FAFB] rounded-3xl shadow-[0_40px_80px_-30px_rgba(124,58,237,0.45)] border border-purple-100 flex flex-col overflow-hidden'
        : 'rc-card relative z-10 w-full max-w-md h-screen sm:h-[92vh] max-h-none sm:max-h-[820px] bg-[#F9FAFB] rounded-none sm:rounded-[2rem] shadow-none sm:shadow-2xl flex flex-col overflow-hidden ring-0 sm:ring-4 sm:ring-white/15'}>
        {/* Header */}
       <div className="flex items-center gap-2.5 sm:gap-3 px-4 py-3 sm:px-5 sm:py-4 bg-white border-b border-gray-100 shrink-0">
  <div className="relative shrink-0">
    <img src={planbLogo} alt="PlanB Jobs" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-contain bg-[#F5E9FE] p-1.5" />
            <span className="rc-online-dot absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="rc-font-display font-bold text-[#3B0764] text-base leading-tight truncate">PlanB Jobs</p>
            <p className="text-xs text-emerald-500 font-medium leading-tight">Your job matchmaker · online</p>
          </div>
          <span className="ml-auto rc-font-display text-[11px] font-bold text-[#7C3AED] bg-[#F5E9FE] px-2 py-1 rounded-full shrink-0">{progressPct}%</span>
                 {embedded && onClose && (
            <button type="button" onClick={onClose} aria-label="Close registration" className="ml-1 text-gray-400 hover:text-[#7C3AED] text-xl leading-none px-1">&times;</button>
          )}
        </div>

        {/* Story-style segmented progress trail */}
        <StoryProgress total={order.length} filled={idx + 1} />

        {/* Conversation */}
           <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 sm:px-4 sm:py-4">
          {messages.map((m) => {
            if (m.from === 'bot' && m.meta?.type === 'already_registered') {
              return (
                <BotBubble key={m.id}>
                  <p className="mb-2">This number is already registered. Please log in to continue.</p>
                  <Link
                    to="/login"
                    state={{ prefillPhone: m.meta.phone }}
                    className="inline-block bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-xs font-semibold px-4 py-2 rounded-full hover:shadow-md transition"
                  >
                    Go to Login →
                  </Link>
                </BotBubble>
              );
            }
            return m.from === 'bot' ? <BotBubble key={m.id}>{m.content}</BotBubble> : <UserBubble key={m.id}>{m.content}</UserBubble>;
          })}
          {typing && <TypingBubble />}
        </div>

        {error && (
          <div className="rc-msg mx-4 mb-2 bg-red-50 text-red-600 border border-red-100 px-3.5 py-2 rounded-2xl text-xs shrink-0">{error}</div>
        )}

               {!typing && (
         <div className="rc-composer border-t border-gray-100 bg-white px-3 py-3 sm:px-4 sm:py-3.5 shrink-0">
            {stepHistory.length > 0  && (
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-[#7C3AED] mb-2 transition-colors"
              >
                ← Back
              </button>
            )}
            {composer()}
          </div>
        )}

       <p className="text-center text-xs text-gray-400 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] bg-white border-t border-gray-50 shrink-0">
          Already have an account?{' '}
          <Link to="/login" className="text-[#7C3AED] font-semibold hover:underline">Login</Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterChat;